import React, { useCallback } from 'react';
import { useDebounceFn, useUpdateEffect } from 'ahooks';
import { transform } from 'lodash';
import { useForm } from 'react-hook-form';

interface Props {
  handleFetchData: (payload: any) => Promise<void>;
  querySearch: { [key: string]: string | object | number };
  queryFilters: { [key: string]: string | object | boolean | null | undefined };
  queryPagination?: { [key: string]: number };
}

export const useTableFilter = (props: Props) => {
  const { handleFetchData, querySearch = {}, queryFilters = {}, queryPagination = {} } = props;
  const methodFilter = useForm({
    defaultValues: {
      ...querySearch,
      ...queryFilters,
      ...queryPagination,
    },
  });

  const { watch, getValues, setValue } = methodFilter;

  const watchInputs = watch(Object.keys(querySearch));

  const watchFilters = watch(Object.keys(queryFilters));

  const watchPagination = watch(Object.keys(queryPagination));

  const trimValue = useCallback((str: any) => {
    if (typeof str !== 'string') return str;
    return str.trim();
  }, []);

  const transformValues = useCallback((values = {}) => {
    const transformed = transform(values, (result: any, value: any, key) => {
      // typeof [] === 'object' too so need another condition
      if (typeof value === 'object' && !Array.isArray(value)) {
        result[key] = trimValue(value?.value);
      } else if (Array.isArray(value)) {
        result[key] = value.map((item) => {
          return trimValue(item);
        });
      } else {
        result[key] = trimValue(value);
      }
    });
    return transformed;
  }, []);

  const debounceFetchData = useDebounceFn(
    () => {
      const params: any = transformValues(getValues());
      handleFetchData({ ...params, page: 1 });
    },
    {
      wait: 500,
    },
  );

  useUpdateEffect(() => {
    setValue('page', 1);
    debounceFetchData.run();
  }, [JSON.stringify(watchInputs)]);

  useUpdateEffect(() => {
    const params = transformValues(getValues());
    setValue('page', 1);
    handleFetchData({ ...params, page: 1 });
  }, [JSON.stringify(watchFilters)]);

  useUpdateEffect(() => {
    const params = transformValues(getValues());
    handleFetchData({ ...params });
  }, [JSON.stringify(watchPagination)]);

  return {
    methodFilter,
    setValue,
  };
};
